import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DailyBriefCard } from "@/components/DailyBriefCard";
import { BusinessPulse } from "@/components/BusinessPulse";
import { InsightsRecommendationsFeed } from "@/components/InsightsRecommendationsFeed";
import { ByTheWayNudge } from "@/components/ByTheWayNudge";
import { formatDateTime, formatDate } from "@/lib/helpers";
import { Users, UserCog, FileText, Wrench, CalendarDays, School, Check, X, ArrowRight, Bell } from "lucide-react";
import { toast } from "sonner";

// Hoisted outside the component: these are static (no props/state
// dependency) so recreating them on every render served no purpose.
const FADE_UP_INITIAL = { opacity: 0, y: 8 };
const FADE_UP_ANIMATE = { opacity: 1, y: 0 };
const FADE_UP_TRANSITION = { duration: 0.2 };

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reschedules, setReschedules] = useState([]);

  const load = useCallback(async () => {
    try {
      const [dashRes, reschedRes] = await Promise.all([
        api.get("/dashboard/admin"),
        api.get("/reschedule-requests"),
      ]);
      setData(dashRes.data);
      setReschedules((reschedRes.data || []).filter((r) => r.status === "pending"));
    } catch {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolveReschedule = async (id, action) => {
    try {
      await api.put(`/reschedule-requests/${id}`, { action });
      toast.success(action === "approve" ? "Reschedule approved" : "Reschedule rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-[var(--radius)]" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-[var(--radius)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <DailyBriefCard brief={data.daily_brief} />

      <BusinessPulse pulse={data.business_pulse} priorities={data.todays_priorities} />

      <ByTheWayNudge nudge={data.by_the_way} />

      <motion.div
        initial={FADE_UP_INITIAL}
        animate={FADE_UP_ANIMATE}
        transition={FADE_UP_TRANSITION}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <StatCard icon={Users} label="Total Students" value={data.students_count} testId="dashboard-kpi-card-students" accent />
        <StatCard icon={UserCog} label="Active Instructors" value={data.instructors_count} testId="dashboard-kpi-card-instructors" />
        <StatCard icon={FileText} label="Pending Forms" value={data.pending_forms} testId="dashboard-kpi-card-pending-forms" />
        <StatCard icon={Wrench} label="Open Vehicle Issues" value={data.open_vehicle_issues?.length || 0} testId="dashboard-kpi-card-vehicle-issues" />
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-[var(--radius)] border bg-card">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b">
              <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-[hsl(var(--role-accent))]" /> Today&apos;s Driving Schedule
              </h2>
              <Link to="/schedule" className="text-sm text-[hsl(var(--role-accent))] font-medium flex items-center gap-1" data-testid="dashboard-view-schedule-link">
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="divide-y">
              {data.todays_schedule.length === 0 && (
                <p className="p-6 text-sm text-muted-foreground text-center">No lessons scheduled for today.</p>
              )}
              {data.todays_schedule.map((appt) => (
                <div key={appt.id} className="flex items-center justify-between gap-3 p-4 sm:p-5" data-testid="dashboard-today-lesson-row">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{appt.student_name || "Lesson"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {formatDateTime(appt.start_time)} &middot; Instructor: {appt.instructor_name}
                    </p>
                  </div>
                  <StatusBadge status={appt.status} />
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[var(--radius)] border bg-card">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b">
              <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
                <School className="h-5 w-5 text-[hsl(var(--role-accent))]" /> Upcoming Classroom Sessions
              </h2>
              <Link to="/classrooms" className="text-sm text-[hsl(var(--role-accent))] font-medium flex items-center gap-1" data-testid="dashboard-view-classrooms-link">
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="divide-y">
              {data.upcoming_classroom_sessions.length === 0 && (
                <p className="p-6 text-sm text-muted-foreground text-center">No classroom sessions scheduled.</p>
              )}
              {data.upcoming_classroom_sessions.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-4 sm:p-5" data-testid="dashboard-classroom-row">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{c.class_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {c.location} &middot; {(c.dates || [])[0] ? formatDate(c.dates[0]) : "-"} {c.start_time}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground shrink-0">{c.remaining_seats}/{c.capacity} seats</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-[var(--radius)] border bg-card" data-anchor="lesson-requests">
            <div className="p-4 sm:p-5 border-b">
              <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
                <Bell className="h-5 w-5 text-[hsl(var(--role-accent))]" /> Lesson Requests
              </h2>
            </div>
            <div className="divide-y">
              {reschedules.length === 0 && (
                <p className="p-6 text-sm text-muted-foreground text-center">
                  No pending requests — a good time to review upcoming lessons or reach out to prospective students.
                </p>
              )}
              {reschedules.map((r) => (
                <div key={r.id} className="p-4 sm:p-5 space-y-2" data-testid="dashboard-reschedule-request-row">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{r.student_name || "Student"}</p>
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                        r.request_type === "new_lesson"
                          ? "bg-[hsl(var(--status-scheduled))]/10 text-[hsl(var(--status-scheduled))]"
                          : "bg-[hsl(var(--status-attention))]/10 text-[hsl(var(--status-attention))]"
                      }`}
                      data-testid="dashboard-reschedule-request-type-badge"
                    >
                      {r.request_type === "new_lesson" ? "New Lesson" : "Reschedule"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{r.reason || "No reason provided"}</p>
                  {r.preferred_time && <p className="text-xs text-muted-foreground">Preferred: {r.preferred_time}</p>}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={() => resolveReschedule(r.id, "approve")} data-testid="reschedule-approve-button">
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => resolveReschedule(r.id, "reject")} data-testid="reschedule-reject-button">
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[var(--radius)] border bg-card">
            <div className="p-4 sm:p-5 border-b">
              <h2 className="text-lg font-semibold font-heading flex items-center gap-2">
                <Wrench className="h-5 w-5 text-[hsl(var(--role-accent))]" /> Open Vehicle Issues
              </h2>
            </div>
            <div className="divide-y">
              {data.open_vehicle_issues.length === 0 && (
                <p className="p-6 text-sm text-muted-foreground text-center">
                  All vehicles are operating normally — no issues need attention right now.
                </p>
              )}
              {data.open_vehicle_issues.slice(0, 6).map((i) => (
                <div key={i.id} className="p-4 sm:p-5" data-testid="dashboard-vehicle-issue-row">
                  <p className="text-sm font-medium truncate">{i.description}</p>
                  <p className="text-xs text-muted-foreground">By {i.reported_by_name} &middot; {formatDateTime(i.created_at)}</p>
                </div>
              ))}
              {data.open_vehicle_issues.length > 0 && (
                <div className="p-3">
                  <Link to="/vehicle-issues" className="text-sm text-[hsl(var(--role-accent))] font-medium flex items-center gap-1 justify-center">
                    View all issues <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <InsightsRecommendationsFeed items={data.insights} />
    </div>
  );
}
